import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Col, Form, Input, Row, Space, Switch } from "antd";
import { has } from "lodash-es";
import { observer } from "mobx-react-lite";
import { FC } from "react";
import { Controller, useForm } from "react-hook-form";
import { useIntl } from "react-intl";

import ErrorsFormatter from "@/ui/_common_/errors-formatter";
import { FormSpinner } from "@/ui/_common_/form-spinner";

import { FormState } from "./FormState";
import { ISchema, useSchema } from "./schema";

type Props = {
  state: FormState;
};

export const FormView: FC<Props> = observer(({ state }) => {
  const intl = useIntl();
  const schema = useSchema();
  const form = useForm<ISchema>({
    resolver: yupResolver(schema),
  });

  if (
    state.hallQuery.isIdle ||
    state.hallQuery.isPending ||
    state.cashierQuery.isIdle ||
    state.cashierQuery.isPending
  ) {
    return <FormSpinner />;
  }

  if (state.hallQuery.isRejected || state.cashierQuery.isRejected) {
    return <ErrorsFormatter queries={[state.hallQuery, state.cashierQuery]} />;
  }

  return (
    <Space direction="vertical">
      <ErrorsFormatter queries={[state.startShiftQuery]} />
      <Form
        labelCol={{ span: 8 }}
        labelAlign="left"
        wrapperCol={{ span: 16 }}
        layout="horizontal"
        onFinish={form.handleSubmit(state.handleSubmit)}
      >
        <Form.Item label={intl.formatMessage({ defaultMessage: "Cashier" })}>
          <Input disabled value={state.cashier?.name} />
        </Form.Item>
        <Form.Item
          label={intl.formatMessage({ defaultMessage: "Cash in register" })}
          validateStatus={
            has(form.formState.errors, "beginningCash") ? "error" : ""
          }
          help={form.formState.errors.beginningCash?.message}
        >
          <Controller
            control={form.control}
            name="beginningCash"
            render={({ field }) => (
              <Input
                placeholder={intl.formatMessage({
                  defaultMessage: "Enter cash in register",
                })}
                {...field}
                type="number"
                autoFocus
              />
            )}
          />
        </Form.Item>
        <Form.Item
          label={intl.formatMessage({ defaultMessage: "Print receipt" })}
        >
          <Controller
            control={form.control}
            name="printReceipt"
            defaultValue={false}
            render={({ field }) => <Switch {...field} checked={field.value} />}
          />
        </Form.Item>
        <Row justify="end" gutter={12}>
          <Col>
            <Button
              type="primary"
              htmlType="submit"
              loading={
                state.startShiftQuery.isPending ||
                state.userStore.cashierQuery.isPending ||
                state.userStore.shiftQuery.isPending
              }
            >
              {intl.formatMessage({ defaultMessage: "Open shift" })}
            </Button>
          </Col>
        </Row>
      </Form>
    </Space>
  );
});
